import { ArrowUpRight, Check, Mail } from "lucide-react";
import { APP_URL } from "@/components/SiteNav";
import { INSIGHTS_MAILTO } from "@/components/aziende/plans";
import { euro } from "@/lib/format";

const Price = ({ plan, pcto }) => {
  const { standard, pcto: fsl } = plan.price;
  const discounted = pcto && standard !== fsl;
  const current = pcto ? fsl : standard;
  return (
    <div className="mt-8 min-h-[112px] flex flex-col justify-end">
      <span className={`text-sm text-[#5b6068] line-through transition-opacity duration-300 ${discounted ? "opacity-100" : "opacity-0"}`} aria-hidden={!discounted}>
        {euro(standard)} /anno
      </span>
      <p className="flex items-baseline gap-2" data-testid={`price-${plan.id}`}>
        <span key={pcto ? "pcto" : "std"} className="price-swap font-extrabold tracking-[-0.04em] text-4xl xl:text-[2.6rem]">
          {euro(current)}
        </span>
        <span className="text-sm text-titanium">/anno</span>
      </p>
      <p className="mt-1.5 text-sm text-titanium" data-testid={`price-monthly-${plan.id}`}>
        {euro(current / 12)}/mese · IVA esclusa
      </p>
    </div>
  );
};

const InsightsPrice = ({ price }) => (
  <div className="mt-8 min-h-[112px] flex flex-col justify-end">
    <p className="flex items-baseline gap-2" data-testid="price-insights">
      <span className="font-extrabold tracking-[-0.04em] text-4xl xl:text-[2.6rem]">{euro(price.monthly)}</span>
      <span className="text-sm text-titanium">/mese</span>
    </p>
    <p className="mt-1.5 text-sm text-titanium">oppure {euro(price.yearly)}/anno · IVA esclusa</p>
    <p className="mt-1 text-xs text-[#6b7078]" data-testid="price-insights-note">Nessuno sconto ospitalità</p>
  </div>
);

const Badges = ({ badges = [] }) => (
  <div className={`mb-4 min-h-[26px] flex-wrap gap-1.5 ${badges.length ? "flex" : "hidden md:flex"}`}>
    {badges.map((b) => (
      <span key={b} className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold tracking-[0.18em] uppercase ${b === "BETA" ? "beta-pill" : "border-[#00e5ff]/60 text-cyan"}`} data-testid={`plan-badge-${b}`}>
        {b}
      </span>
    ))}
  </div>
);

const Cta = ({ plan }) => {
  const isInsights = plan.kind === "insights";
  const ext = isInsights ? { href: INSIGHTS_MAILTO } : { href: APP_URL, target: "_blank", rel: "noopener noreferrer" };
  return (
    <a {...ext} className="cta cta-glow glass mt-8 inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white" data-testid={`contact-${plan.id}`}>
      {isInsights ? <><Mail className="h-4 w-4 text-cyan" /> Richiedi l'attivazione</> : <>Richiedi l'accesso <ArrowUpRight className="h-4 w-4 text-cyan" /></>}
    </a>
  );
};

export const PricingCard = ({ plan, pcto }) => {
  const isInsights = plan.kind === "insights";
  return (
    <article
      className={`pricing-card glass glass-light relative h-full rounded-[28px] p-7 sm:p-8 flex flex-col ${plan.featured ? "is-featured" : ""} ${isInsights ? "is-insights" : ""}`}
      data-testid={`plan-card-${plan.id}`}
    >
      <Badges badges={plan.badges} />
      <h3 className="font-bold tracking-[-0.03em] text-2xl">{plan.name}</h3>
      <p className="mt-2 text-sm text-titanium">{plan.subtitle}</p>
      {isInsights ? <InsightsPrice price={plan.insightsPrice} /> : <Price plan={plan} pcto={pcto} />}
      {plan.pitch && <p className="mt-6 text-base font-semibold leading-snug text-white" data-testid="plan-insights-pitch">{plan.pitch}</p>}
      <ul className={`${plan.pitch ? "mt-6" : "mt-8"} pt-8 border-t border-[#1e2024] space-y-3.5 flex-1`}>
        {plan.features.map((f) => (
          <li key={f} className="flex gap-3 text-sm leading-relaxed text-white/85">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan" strokeWidth={2} />
            {f}
          </li>
        ))}
        {plan.insights?.map((f) => (
          <li key={`ins-${f}`} className="flex gap-3 text-sm leading-relaxed font-medium text-white" data-testid={`plan-insights-item-${plan.id}`}>
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan" strokeWidth={2} />
            <span>
              {f}{" "}
              <span className="beta-pill ml-1 inline-block rounded-full border px-1.5 py-px align-middle text-[9px] font-semibold tracking-[0.14em] uppercase">Beta</span>
            </span>
          </li>
        ))}
      </ul>
      {plan.note && <p className="mt-6 text-[11px] leading-relaxed text-[#6b7078]" data-testid="plan-insights-disclaimer">{plan.note}</p>}
      <Cta plan={plan} />
    </article>
  );
};
